'use client';

import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { DateRange as ReactDateRange } from 'react-day-picker';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface DateRangeValue {
  from: Date | undefined;
  to?: Date | undefined;
}

type Props = {
  disabled?: boolean;
  className?: string;
  classNameButton?: string;
  size?: 'sm' | 'default' | 'lg';
  value: DateRangeValue | undefined;
  dateFormat?: string;
  applyButton?: boolean;
  applyButtonValidate?: (range: ReactDateRange | undefined) => boolean;
  onChange?: (range: ReactDateRange | undefined) => void;
  onCommit?: (range: ReactDateRange | undefined) => void;
};

export default function DateRange({
  disabled,
  className,
  classNameButton,
  size,
  value,
  dateFormat = 'LLL dd, y',
  applyButton,
  applyButtonValidate,
  onChange,
  onCommit,
}: Props) {
  const [date, setDate] = React.useState<ReactDateRange | undefined>(value);

  React.useEffect(() => {
    setDate(value);
  }, [value]);

  const handleChange = (range: ReactDateRange | undefined) => {
    setDate(range);
    onChange?.(range);
  };

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover
        onOpenChange={(open) => {
          if (!open) {
            if (applyButtonValidate) {
              if (applyButtonValidate(date)) {
                onCommit?.(date);
              }
            } else {
              onCommit?.(date);
            }
          }
        }}
      >
        <PopoverTrigger asChild disabled={disabled}>
          <Button
            id='date'
            variant='outline'
            size={size}
            className={cn(
              'w-[300px] justify-start text-left font-normal',
              !date && 'text-muted-foreground',
              classNameButton
            )}
            disabled={disabled}
          >
            <CalendarIcon />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, dateFormat)} - {format(date.to, dateFormat)}
                </>
              ) : (
                format(date.from, dateFormat)
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='start'>
          <div className='flex w-full items-center justify-between border-b-[0.5px] px-4 py-2'>
            <div className='text-sm font-medium'>Select Date Range</div>
            <Button variant='ghost' size='sm' onClick={() => setDate(undefined)}>
              Clear
            </Button>
          </div>
          <Calendar
            mode='range'
            defaultMonth={date?.from}
            selected={date}
            onSelect={handleChange}
            numberOfMonths={2}
            showOutsideDays={false}
          />
          {applyButton && (
            <div className='flex w-full justify-end p-4 pt-2'>
              <Button
                disabled={applyButtonValidate ? !applyButtonValidate(date) : !date?.from}
                onClick={() => {
                  onCommit?.(date);
                }}
              >
                Apply
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <input name='from' type='hidden' value={date?.from ? format(date.from, 'yyyy-MM-dd') : ''} />
      <input name='to' type='hidden' value={date?.to ? format(date.to, 'yyyy-MM-dd') : ''} />
    </div>
  );
}
